import { downloadCSV, getAPI, postAPI, postMultipartAPI } from "../../../api/Networking";
import {
  APIBlobResponse,
  APIResponse,
  PaginatedAPIResponse,
} from "../../../models/NetworkResponse";
import { SwageazyInventoryFilters } from "../../../models/SwageazyInventory/SwageazyInventoryFilter";
import {
  SwageazyInventoryDetails,
  SwageazyInventoryInwardRequestFormData,
  SwageazyInventorySampleReturnRequestFormData,
  SwageazyInventoryResolveBoxesRequest,
  SwageazyInventoryResolveBoxesResult,
  ProductStockDetails,
  ProductAvailableQuantity,
  BlockedQuantityDetailsModel,
  PurchaseOrderProductDetails,
  SampleSirReturnOption,
  BoxTransferStockLine,
  BoxTransferRequest,
} from "../../../models/SwageazyInventory/SwageazyInventory";
import { WarehouseBoxOption } from "../../../models/Warehouse/Warehouse";
import { PurchaseRequestBody } from "../../../models/PurchaseRequest/PurchaseRequestBody";
import { PurhcaseRequestDetails } from "../../../models/PurchaseRequest/PurchaseRequestDetails";
import { ProductSourcingSkuStatusInfo } from "../../../models/ProjectManagement/ProjectPackagingSwagpacks";

export default class SwageazyInventoryAPI {
  getSwageazyInventoryListing = async (
    filters: SwageazyInventoryFilters,
  ): Promise<PaginatedAPIResponse<SwageazyInventoryDetails>> => {
    try {
      const queryParams = new URLSearchParams();

      if (filters.page_no !== undefined)
        queryParams.append("page_no", filters.page_no.toString());

      if (filters.limit !== undefined)
        queryParams.append("limit", filters.limit.toString());

      if (filters.search_text)
        queryParams.append("search_text", filters.search_text);

      if (filters.warehouse_id)
        queryParams.append("warehouse_id", filters.warehouse_id);

      if (filters.stock_status)
        queryParams.append("stock_status", filters.stock_status);

      if (filters.available_qty_min !== undefined)
        queryParams.append(
          "available_qty_min",
          filters.available_qty_min.toString(),
        );

      if (filters.available_qty_max !== undefined)
        queryParams.append(
          "available_qty_max",
          filters.available_qty_max.toString(),
        );

      if (filters.sort_by) queryParams.append("sort_by", filters.sort_by);

      if (filters.sort_type) queryParams.append("sort_type", filters.sort_type);

      const response = await getAPI(
        `/v5/inventories/swageazy?${queryParams.toString()}`,
      );

      return response.result;
    } catch (error) {
      throw error;
    }
  };

  downloadSwageazyInventoryReport = async (
    filters: SwageazyInventoryFilters,
  ): Promise<APIBlobResponse> => {
    try {
      const queryParams = new URLSearchParams();

      if (filters.search_text)
        queryParams.append("search_text", filters.search_text);

      if (filters.warehouse_id)
        queryParams.append("warehouse_id", filters.warehouse_id);

      if (filters.stock_status)
        queryParams.append("stock_status", filters.stock_status);

      if (filters.available_qty_min !== undefined)
        queryParams.append(
          "available_qty_min",
          filters.available_qty_min.toString(),
        );

      if (filters.available_qty_max !== undefined)
        queryParams.append(
          "available_qty_max",
          filters.available_qty_max.toString(),
        );

      queryParams.append("download_report", true.toString());
      const response = await downloadCSV(
        `/v5/inventories/swageazy/download?${queryParams.toString()}`,
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  createSwageazyInventoryPurchaseRequest = async (
    data: PurchaseRequestBody,
  ): Promise<APIResponse> => {
    try {
      const response = await postAPI(
        `/admin/purchaseRequest/saveSwageazyRequest`,
        {
          purchase_request: data,
        },
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  getPurchaseOrderNumbers = async (
    searchText: string,
  ): Promise<PurchaseOrderProductDetails> => {
    try {
      const response = await getAPI(
        `/v5/inventories/swageazy/purchase-order-products?page_no=1&search_text=${searchText}&limit=100`,
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  generateBoxId = async (
    current_rack: string,
    warehouse_id: string,
  ): Promise<APIResponse> => {
    try {
      const response = await postAPI(`/v5/inventories/warehouse/boxes`, {
        current_rack: current_rack,
        warehouse_id: warehouse_id,
      });
      return response;
    } catch (error) {
      throw error;
    }
  };

  createSwageazyInventoryInwardTransfer = async (
    data: SwageazyInventoryInwardRequestFormData,
  ): Promise<APIResponse> => {
    try {
      const response = await postAPI(`/v5/inventories/swageazy/credit`, data);
      return response;
    } catch (error) {
      throw error;
    }
  };

  resolveOutwardBoxes = async (
    data: SwageazyInventoryResolveBoxesRequest,
  ): Promise<SwageazyInventoryResolveBoxesResult> => {
    try {
      const response = await postAPI(
        `/v5/inventories/swageazy/debit/resolve-boxes`,
        data,
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  createSwageazyInventoryOutwardTransfer = async (data: {
    po_id: number;
    warehouse_id: string;
    quantity_details: Array<{
      product_id: number;
      sku: string;
      box_id: string;
      quantity: number;
    }>;
  }): Promise<APIResponse> => {
    try {
      const response = await postAPI(`/v5/inventories/swageazy/debit`, data);
      return response;
    } catch (error) {
      throw error;
    }
  };

  sampleReturn = async (
    data: SwageazyInventorySampleReturnRequestFormData,
  ): Promise<APIResponse> => {
    try {
      const response = await postAPI(`/v5/inventories/swageazy/return`, data);
      return response;
    } catch (error) {
      throw error;
    }
  };

  fetchSampleReturnSirs = async (
    orderId: number,
  ): Promise<SampleSirReturnOption[]> => {
    try {
      const response = await getAPI(
        `/v5/inventories/swageazy/sample-return/order/${orderId}/sirs`,
      );
      return response.result ?? [];
    } catch (error) {
      throw error;
    }
  };

  fetchSampleReturnSkuLines = async (
    orderId: number,
    stockIssueRequestId?: number,
  ): Promise<
    Array<{
      id: number;
      product_id?: number;
      sku?: string;
      product_name?: string | null;
      expected_quantity?: number;
      returned_quantity?: number;
      pending_quantity?: number;
      quantity?: number;
    }>
  > => {
    try {
      const query =
        stockIssueRequestId != null
          ? `?stock_issue_request_id=${stockIssueRequestId}`
          : "";
      const response = await getAPI(
        `/v5/inventories/swageazy/sample-return/order/${orderId}/sku-lines${query}`,
      );
      return response.result ?? [];
    } catch (error) {
      throw error;
    }
  };

  BlockedQuantityDetails = async (
    po_id: number,
    product_id: number,
    warehouse_id: number | string,
  ): Promise<BlockedQuantityDetailsModel> => {
    try {
      const response = await postAPI(`/v5/inventories/swageazy/stock/blocked`, {
        po_id: po_id,
        product_id: product_id,
        warehouse_id: warehouse_id,
      });
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  getProductStockDetails = async (
    product_id: number,
  ): Promise<ProductStockDetails> => {
    try {
      const response = await getAPI(
        `/v5/inventories/swageazy/product/${product_id}`,
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  getProductsAvailableQuantity = async (
    product_ids: number[],
  ): Promise<ProductAvailableQuantity[]> => {
    try {
      const uniqueProductIds = Array.from(
        new Set(product_ids.filter(Boolean)),
      );
      if (uniqueProductIds.length === 0) {
        return [];
      }

      const response = await postAPI(
        `/v5/inventories/swageazy/products/available-quantity`,
        { product_ids: uniqueProductIds },
      );
      return (response.result?.products ?? []) as ProductAvailableQuantity[];
    } catch (error) {
      throw error;
    }
  };

  getProductTransactions = async (product_id: number): Promise<APIResponse> => {
    try {
      const response = await getAPI(
        `/v5/inventories/swageazy/product/${product_id}/transactions`,
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  uploadInventoryVariantSkuCsv = async (file: File): Promise<APIResponse> => {
    try {
      if (!file) {
        throw new Error("No file provided for upload.");
      }

      const formData = new FormData();
      formData.append("csv_file", file);

      return await postMultipartAPI(
        `/v1/swageazy-inventory/inventory-variants-logs/sku/import-csv`,
        formData,
      );
    } catch (error) {
      throw error;
    }
  };

  getPurchaseRequestDetailByPOId = async (
    poId: number,
  ): Promise<PurhcaseRequestDetails> => {
    try {
      const response = await getAPI(
        `/v5/purchase-requests/details/po-id/${poId}`,
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  getPurchaseRequestDetailsByOrderIdAndProductId = async (
    orderId: number,
    productId: number,
  ): Promise<PurhcaseRequestDetails[]> => {
    try {
      const response = await getAPI(
        `/v5/purchase-requests/details/order-id/${orderId}/product-id/${productId}`,
      );
      return response.result || [];
    } catch (error) {
      throw error;
    }
  };

  getBoxStock = async (
    boxId: string,
    warehouseId: string,
  ): Promise<BoxTransferStockLine[]> => {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append("warehouse_id", warehouseId);
      const response = await getAPI(
        `/v1/swageazy-inventory/boxes/${encodeURIComponent(boxId)}/stock?${queryParams.toString()}`,
      );
      return response.result || [];
    } catch (error) {
      throw error;
    }
  };

  createBoxTransfer = async (
    data: BoxTransferRequest,
  ): Promise<APIResponse> => {
    try {
      return await postAPI(`/v1/swageazy-inventory/box-transfer`, data);
    } catch (error) {
      throw error;
    }
  };

  listWarehouseRacks = async (
    warehouseId: string,
  ): Promise<{ id: string; warehouse_id: string }[]> => {
    try {
      const response = await getAPI(
        `/v1/swageazy-inventory/warehouses/${encodeURIComponent(warehouseId)}/racks`,
      );
      return response.result || [];
    } catch (error) {
      throw error;
    }
  };

  createWarehouseRack = async (data: {
    warehouse_id: string;
    rack_id?: string;
  }): Promise<APIResponse> => {
    try {
      return await postAPI(`/v1/swageazy-inventory/racks`, data);
    } catch (error) {
      throw error;
    }
  };

  createRackTransfer = async (data: {
    warehouse_id: string;
    source_rack_id: string;
    dest_rack_id: string;
    box_ids: string[];
    reason?: string;
  }): Promise<APIResponse> => {
    try {
      return await postAPI(`/v1/swageazy-inventory/rack-transfer`, data);
    } catch (error) {
      throw error;
    }
  };

  getPurchaseRequestInwardBySku = async (
    prId: number,
  ): Promise<ProductSourcingSkuStatusInfo[]> => {
    try {
      const response = await getAPI(
        `/v1/swageazy-inventory/purchase-requests/${prId}/inward-by-sku`,
      );
      return response.result ?? [];
    } catch (error) {
      throw error;
    }
  };

  listWarehouseBoxes = async (
    warehouseId: string,
  ): Promise<WarehouseBoxOption[]> => {
    try {
      const response = await getAPI(
        `/v1/swageazy-inventory/warehouses/${encodeURIComponent(warehouseId)}/boxes`,
      );
      return response.result || [];
    } catch (error) {
      throw error;
    }
  };
}
